import type { Dispatch } from '@reduxjs/toolkit';
import debounce from 'lodash/debounce';

import { MAX_SHARING_ZOOM, MIN_SHARING_ZOOM } from './constants';
import { setSharingZoom } from './slice';

const DEBOUNCE_DELAY_MS = 100;

/**
 * Вычисляет максимально допустимое смещение по одной оси так, чтобы
 * масштабированный элемент не выходил за пределы своего контейнера.
 */
function calcMaxTranslate(size: number, scale: number): number {
    if (scale <= 1) {
        return 0;
    }
    return ((scale - 1) / 2 / scale) * size;
}

/**
 * Контроллер приближения и панорамирования видео-элемента участника.
 *
 * Применяет CSS-трансформацию к `videoElementRef` через `requestAnimationFrame`
 * и сохраняет состояние в Redux с debounce.
 *
 * Смещение автоматически ограничивается так, чтобы края видео-элемента
 * никогда не отрывались от краёв контейнера.
 */
export class SharingZoomController {
    private readonly videoElement: HTMLVideoElement;
    private readonly dispatch: Dispatch;

    private rafId: number | null = null;
    private readonly debouncedDispatch: ReturnType<typeof debounce>;

    constructor(videoElement: HTMLVideoElement, dispatch: Dispatch) {
        this.videoElement = videoElement;
        this.dispatch = dispatch;

        this.debouncedDispatch = debounce((scale: number, tx: number, ty: number) => {
            this.dispatch(setSharingZoom({ scale, tx, ty }));
        }, DEBOUNCE_DELAY_MS);
    }

    /**
     * Применяет масштаб и смещение к видео-элементу.
     *
     * - `scale` ограничивается диапазоном [MIN_SHARING_ZOOM, MAX_SHARING_ZOOM].
     * - `tx` / `ty` зажимаются так, чтобы края элемента не выходили за пределы
     *   контейнера.
     */
    zoom(scale: number, tx: number, ty: number, immediate?: boolean): void {
        const clampedScale = Math.min(MAX_SHARING_ZOOM, Math.max(MIN_SHARING_ZOOM, scale));

        const width = this.videoElement.offsetWidth;
        const height = this.videoElement.offsetHeight;
        const maxTx = calcMaxTranslate(width, clampedScale);
        const maxTy = calcMaxTranslate(height, clampedScale);

        const clampedTx = Math.min(maxTx, Math.max(-maxTx, tx));
        const clampedTy = Math.min(maxTy, Math.max(-maxTy, ty));

        // Применяем трансформацию через requestAnimationFrame
        if (this.rafId !== null) {
            cancelAnimationFrame(this.rafId);
        }

        this.rafId = requestAnimationFrame(() => {
            this.videoElement.style.transform = `scale(${clampedScale}) translate(${clampedTx}px, ${clampedTy}px)`;
            this.rafId = null;
        });

        if (immediate) {
            this.dispatch(setSharingZoom({ scale: clampedScale, tx: clampedTx, ty: clampedTy }));
        } else {
            this.debouncedDispatch(clampedScale, clampedTx, clampedTy);
        }
    }

    /**
     * Сбрасывает приближение и положение к исходным значениям.
     */
    reset(): void {
        this.zoom(1, 0, 0, true);
    }

    /**
     * Освобождает ресурсы (pending RAF и debounce-таймер).
     * Следует вызывать при размонтировании компонента.
     */
    destroy(): void {
        if (this.rafId !== null) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }

        this.debouncedDispatch.cancel();
    }
}
